import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { ErrorCode, OpaqueTokenSchema, WEB_ROUTES } from '@lsc/shared';
import { apiRequest } from '../../api/client';
import { ApiError } from '../../api/errors';
import { LoadingState } from '../../components/LoadingState';
import { publicQueryKeys } from '../api';
import { ErrorState } from '../components/ErrorState';

const TOKEN_PARAM = 't';

/**
 * `/verify?t=…` – email verification (§17, §21). The token is removed from the address bar and
 * history before anything else happens and redeemed via POST (no state change on GET).
 */
export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Read once: the URL is stripped right after the first render.
  const [token] = useState(() => searchParams.get(TOKEN_PARAM));
  const [tokenValid] = useState(() => OpaqueTokenSchema.safeParse(token).success);
  const started = useRef(false);

  const { mutate, error, isError } = useMutation({
    mutationFn: (value: string) => apiRequest('verifyEmail', { body: { token: value } }),
    onSuccess: (data) => {
      queryClient.setQueryData(publicQueryKeys.registrationStatus, data);
      void navigate(WEB_ROUTES.registrationStatus, { replace: true });
    },
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Replaces the history entry → the token is neither visible nor in the back stack.
    void navigate(WEB_ROUTES.verify, { replace: true });
    if (tokenValid && token !== null) mutate(token);
  }, [navigate, mutate, token, tokenValid]);

  if (!tokenValid) {
    return (
      <ErrorState
        error={
          new ApiError({ code: ErrorCode.VERIFICATION_TOKEN_INVALID, status: 400, requestId: null })
        }
      />
    );
  }
  if (isError) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          if (token !== null) mutate(token);
        }}
      />
    );
  }
  return <LoadingState label="Ihre E-Mail-Adresse wird bestätigt …" />;
}
